import type {CSSProperties} from 'react';
import {WEDGE_SETTINGS_ACTION as A} from '@territory/tokens';
import {font, radius, TOUCH_MIN, weight} from '../scale.js';
import {wedgeExactRole, wedgeRole} from './wedgeRoles.js';
import {wedgeSavedSheetStyle} from './wedgeSavedSheetButton.js';

export type WedgeSavedFolderLook = 'saved-folder-bare-suggest' | 'saved-folder-row' | 'saved-folder-row-on' | 'saved-folder-rename' | 'saved-folder-save' | 'saved-folder-cancel' | 'saved-folder-create' | 'saved-folder-remove' | 'saved-folder-bare-delete';
export type WedgeSavedFolderFieldVariant = 'wedge-saved-folder-rename-field' | 'wedge-saved-folder-new-field';

const FOLDER_LOOKS = new Set<string>(['saved-folder-bare-suggest', 'saved-folder-row', 'saved-folder-row-on', 'saved-folder-rename', 'saved-folder-save', 'saved-folder-cancel', 'saved-folder-create', 'saved-folder-remove', 'saved-folder-bare-delete']);
export const isWedgeSavedFolderLook = (look: string): look is WedgeSavedFolderLook => FOLDER_LOOKS.has(look);
export const isWedgeSavedFolderFieldVariant = (variant: unknown): variant is WedgeSavedFolderFieldVariant =>
  variant === 'wedge-saved-folder-rename-field' || variant === 'wedge-saved-folder-new-field';

/* Released move-to-folder sheet of the Saved tab: nine native buttons and two native fields, all in the Wedge font. Every box here is a
   shipped inline style, off-scale numbers included; each color reads the exact legacy role, so the shipped ink rules keep matching. The
   first-folder suggestion is a 32px accent-tint pill (7px by 13px padding, 12.5px semibold type). A folder row is the Set status row of
   wedgeSavedSheetButton with the tint fill when it holds the brief, and it fills the flex row beside Rename (flex 1, min width 0). Rename is
   a 44px bordered box (1px card border, 8px radius, 11px semibold tertiary type, 0 10px padding). Save is a 44px label-fill box (11px
   radius, 0 14px padding, 13.5px semibold, background ink on label) and Cancel the quiet 44px box beside it (0 10px padding, 13.5px medium
   secondary type). Create is a 44px box (0 16px padding, 14px semibold); disabled, it takes the elevated fill, the tertiary ink and the
   default cursor. Remove from folder is a full-width block (12px margin above, 12px padding, 13.5px semibold) in the red role printed with
   its fixed red fallback role, so the shipped ink rules that match the bare red text never retint it, as in the source. Delete
   folder is the quiet block under it (6px margin above, 10px padding, 12px medium tertiary type). The caller owns the glyphs, the labels,
   the handlers and the hidden state of Delete folder. The two fields share one box (flex 1, 11px radius, 11px by 13px padding, 16px type,
   no outline, the elevated fill): rename takes the 0.5px accent border, new folder the 1px card border. The 16px type keeps iOS from zooming.
   Every look but the two folder rows draws no transparent target (wedgeSettingsDrawsHit): see the hit module. */
const BOX_RADIUS = 11, SUGGEST_FONT = 12.5, SUGGEST_HEIGHT = 32, ACTION_FONT = 13.5;
const TAP = {WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation'} as const;

export function wedgeSavedFolderStyle(look: WedgeSavedFolderLook, disabled = false): CSSProperties {
  const r = wedgeExactRole, fontFamily = r('font');
  switch (look) {
    case 'saved-folder-bare-suggest': return {
      background: r('accentTint'), color: r('accent'), border: 0, borderRadius: radius.pill, padding: '7px 13px', fontFamily,
      fontSize: SUGGEST_FONT, fontWeight: weight.semibold, cursor: 'pointer', minHeight: SUGGEST_HEIGHT, ...TAP,
    };
    case 'saved-folder-row': return {...wedgeSavedSheetStyle('saved-sheet-status'), flex: 1, minWidth: 0};
    case 'saved-folder-row-on': return {...wedgeSavedSheetStyle('saved-sheet-status-on'), background: r('accentTint'), flex: 1, minWidth: 0};
    case 'saved-folder-rename': return {
      background: 'transparent', border: `1px solid ${r('cardBorder')}`, borderRadius: radius.sm, color: r('labelTer'), fontFamily,
      fontSize: font.micro, fontWeight: weight.semibold, padding: '0 10px', minHeight: TOUCH_MIN, cursor: 'pointer', flexShrink: 0, ...TAP,
    };
    case 'saved-folder-save': return {
      padding: '0 14px', borderRadius: BOX_RADIUS, minHeight: TOUCH_MIN, background: r('label'), color: r('bg'), border: 0,
      cursor: 'pointer', fontFamily, fontSize: ACTION_FONT, fontWeight: weight.semibold,
    };
    case 'saved-folder-cancel': return {
      padding: '0 10px', borderRadius: BOX_RADIUS, minHeight: TOUCH_MIN, background: 'transparent', color: r('labelSec'), border: 0,
      cursor: 'pointer', fontFamily, fontSize: ACTION_FONT, fontWeight: weight.medium,
    };
    case 'saved-folder-create': return {
      padding: '0 16px', borderRadius: BOX_RADIUS, minHeight: TOUCH_MIN, background: disabled ? r('bgElev') : r('label'),
      color: disabled ? r('labelTer') : r('bg'), border: 0, cursor: disabled ? 'default' : 'pointer', fontFamily, fontSize: font.body,
      fontWeight: weight.semibold, ...TAP,
    };
    case 'saved-folder-remove': return {
      display: 'block', width: '100%', marginTop: 12, background: 'none', border: 0, padding: 12, cursor: 'pointer',
      color: wedgeRole('red', A.redFallback), fontSize: ACTION_FONT, fontWeight: weight.semibold, fontFamily, ...TAP,
    };
    case 'saved-folder-bare-delete': return {
      display: 'block', width: '100%', marginTop: 6, background: 'none', border: 0, padding: 10, cursor: 'pointer',
      color: r('labelTer'), fontSize: font.caption, fontWeight: weight.medium, fontFamily, ...TAP,
    };
  }
}

export function wedgeSavedFolderFieldStyle(variant: WedgeSavedFolderFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '11px 13px', borderRadius: BOX_RADIUS, background: r('bgElev'), color: r('label'),
    border: variant === 'wedge-saved-folder-rename-field' ? `0.5px solid ${r('accent')}` : `1px solid ${r('cardBorder')}`, outline: 'none',
    fontFamily: r('font'), fontSize: font.headline,
  };
}
